import { UnknownAction, configureStore } from "@reduxjs/toolkit";
import { ActionType, PageContentType, SelectedPage } from "./actionTypes";
import { setAppIdReducer } from "./reducer/app";
import { addChartsReducer } from "./reducer/chart";
import { setIdReducer } from "./reducer/chat";
import { addCodeBlocksReducer } from "./reducer/codeBlock";
import { addComponentsReducer } from "./reducer/component";
import { addServiceAccountsReducer } from "./reducer/serviceAccount";
import { initServiceAccountMap, ServiceAccountMap } from "./types/serviceAccount";
import {
  addImplementationsReducer,
  addPageImplementationsReducer,
} from "./reducer/implementation";
import { setShiftDownReducer } from "./reducer/keyboardNavigation";
import {
  addNotebookPagesReducer,
  blockToFocusReducer,
  deleteBlockReducer,
  flushNoteEditsReducer,
  focusNextBlockReducer,
  focusPreviousBlockAction,
  insertBlockReducer,
  moveNoteBlockReducer,
  removeNotebookPagesReducer,
  setBlockIndentLevelReducer,
  setNoteEditReducer,
  setMultiplePageBlocksReducer,
  setPageBlocksReducer,
  setPageNameReducer,
  setPagePublishedReducer,
  setSelectedNotebookPageIdReducer,
} from "./reducer/page";
import {
  deletePropertyTypeReducer,
  setFolderPrimaryKeyAction,
  setFolderPropertiesReducer,
  setFolderSecIndexesReducer,
  setPagePropertyValues,
  setSelectedPropertyReducer,
} from "./reducer/note/property";
import { SetPagePropsAction, setPagePropsReducer } from "./reducer/note/pageProp";
import {
  setPagePageViewsReducer,
  setSelectedViewIdReducer,
  swapPageViewsReducer,
} from "./reducer/note/view";
import { setViewSettingReducer } from "./reducer/note/viewSetting";
import { addProtocolsReducer } from "./reducer/protocol";
import {
  addPullConnectorStatusAction,
  addPullConnectorUrlConfigsAction,
  setSelectedPullConnectorTableAction,
} from "./reducer/pullConnector";
import { addSettingsReducer } from "./reducer/setting";
import { addFullTablesReducer, setTableLoadingReducer } from "./reducer/table";
import { App, AppUser, UserRole } from "./types/app";
import { ChartMap } from "./types/chart";
import { Message } from "./types/chat";
import { CodeBlockMap } from "./types/codeBlock";
import { ComponentMap, initComponentMap } from "./types/component";
import {
  ImplementationMap,
  PageImplementationsMap,
} from "./types/implementation";
import {
  IndentBlockSignal,
  Notebook,
  NotebookPageMap,
  PageBlocksMap,
  PagePublishedMap,
} from "./types/note";
import { NoteEdits } from "./types/notebook/note/block/html";
import { PageToPageViewsMap, PageViewsMap } from "./types/notebook/view";
import { PageViewSettingsMap } from "./types/notebook/viewSetting";
import {
  FolderPrimaryKeyMap,
  FolderPropertyTypesMap as FolderPropertyTypesMap,
  FolderSecIndexesMap,
  PagePropertyValuesMap,
  PropertyTypesMap,
} from "./types/page/property";
import { ProtocolMap, initProtocolMap } from "./types/protocol";
import {
  ConnectCard,
  Connector,
  ConnectorConfigUrlsMap,
  ConnectorStatusMap,
  ConnectorType,
} from "./types/pullConnector";
import {
  PushConnectorFormField,
  PushConnectorMap,
  initialPushConnectorForm,
} from "./types/pushConnector";
import { SettingsMap } from "./types/setting";
import {
  FullTableMap,
  TableLoadingMap,
  TableLogsMap,
  TableMap,
  TableNotFound,
  TableNotFoundMap,
  TablePagesMap,
  TableRunControlMap,
  TableRunHistoryMap,
  TableRunResponses,
  TableSpecMap,
} from "./types/table";
import { PageComponentMap } from "./types/page/component";
import { setPageComponentsReducer } from "./reducer/note/component";
import { PageSubPagesMap } from "./types/sideNav";
import { addSubPagesReducer } from "./reducer/sideNav";
import {
  GetPaymentMethodsResponse,
  GetSubscriptionResponse,
  StripeCustomer,
} from "./types/payment";
import { LogStreamMap } from "./types/logStream";
import { HttpHookMap } from "./types/httpHook";
import { SubscriptionMap } from "./types/subscription";
import { addLogStreamsReducer } from "./reducer/logStream";
import { addHttpHooksReducer, AddHttpHooksAction } from "./reducer/httpHook";
import { addSubscriptionsReducer, AddSubscriptionsAction, clearSubscriptionMapReducer, incrementSubscriptionRefreshReducer } from "./reducer/subscription";
import { RegistrationMap } from "./types/marketplace";
import { addRegistrationsReducer } from "./reducer/marketplace";
import { Package, PackageInstall, PackageMap } from "./types/package";
import {
  addPackagesReducer,
  setInstalledPackagesReducer,
} from "./reducer/package";
import { AiEvent, ConnectionStatus } from "./types/ai-event";

import pagesReducer from "./slices/pages";
import tablesReducer from "./slices/tables";
import connectorsReducer from "./slices/connectors";
import settingsReducer from "./slices/settings";
import uiReducer from "./slices/ui";

export { ActionType, PageContentType } from "./actionTypes";
export type { SelectedPage } from "./actionTypes";

const R = require("ramda");

export interface MainState {
  pageBlocks: PageBlocksMap;
  folderPrimaryKeys: FolderPrimaryKeyMap;
  folderSecIndexes: FolderSecIndexesMap;
  codeBlocks: CodeBlockMap;
  darkMode: boolean;
  settingsMap: SettingsMap;
  pagePropertyValues: PagePropertyValuesMap;
  selectedPropertyName?: string;
  folderPropertyTypes: FolderPropertyTypesMap;
  deleteTables?: string[];
  indentBlock?: IndentBlockSignal;
  blockToFocus?: number;
  tableLoadingMap: TableLoadingMap;
  tableRunControlMap: TableRunControlMap;
  tableRunHistoryMap: TableRunHistoryMap;
  notFoundTables: TableNotFoundMap;
  selectedPullConnector?: string;
  noteEdits: NoteEdits;
  tableLogsMap: TableLogsMap;
  tableRunResponses: TableRunResponses;
  connectorStatusMap: ConnectorStatusMap;
  appOwner?: string;
  stripeCustomer?: StripeCustomer;
  paymentMethods?: GetPaymentMethodsResponse;
  stripeSubscription?: GetSubscriptionResponse;
  roles: UserRole[];
  workspaceUsers: AppUser[];
  allUsers: AppUser[];
  initialPageLoaded?: boolean;
  notebook?: Notebook;
  notebookPages: NotebookPageMap;
  selectedNotebookPageId?: string;
  allWorkspaces: App[];
  apps: App[];
  appId?: string;
  selectedPage: SelectedPage;
  chatId?: string;
  chatDrawerOpen: boolean;
  sideNavOpen: boolean;
  messages: Message[];
  connectors: Connector[];
  drawerWidth: string;
  tables: TableMap;
  connectorTables: any;
  chatLoading: boolean;
  connectorTypes: ConnectorType[];
  selectedConnectorType?: ConnectorType;
  selectedConnectCard?: ConnectCard;
  authToken?: string;
  pushConnectors: PushConnectorMap;
  selectedPushConnector?: string;
  pushConnectorForm: PushConnectorFormField[];
  noWorkspaces?: boolean;
  tablePages: TablePagesMap;
  tableSpecs: TableSpecMap;
  pageViews: PageViewsMap;
  pageToPageViews: PageToPageViewsMap;
  selectedViewId?: string;
  pageViewSettings: PageViewSettingsMap;
  charts: ChartMap;
  fullTables: FullTableMap;
  connectorConfigUrls: ConnectorConfigUrlsMap;
  isShiftDown: boolean;
  publishedPagesMap: PagePublishedMap;
  selectedPullConnectorTableId?: string;
  components: ComponentMap;
  serviceAccounts: ServiceAccountMap;
  protocols: ProtocolMap;
  implementations: ImplementationMap;
  pageComponents: PageComponentMap;
  pageImplementations: PageImplementationsMap;
  pageSubPagesMap: PageSubPagesMap;
  logStreamMap: LogStreamMap;
  httpHookMap: HttpHookMap;
  subscriptionMap: SubscriptionMap;
  subscriptionRefreshKey: number;
  registrations: RegistrationMap;
  packages: PackageMap;
  installedPackages: PackageInstall[];
  aiPanelOpen: boolean;
  aiPanelFullscreen: boolean;
  aiEvents: AiEvent[];
  aiConnectionStatus: ConnectionStatus;
}

const initialState: MainState = {
  pageBlocks: {},
  folderPrimaryKeys: {},
  folderSecIndexes: {},
  registrations: {},
  packages: {},
  installedPackages: [],
  codeBlocks: {},
  darkMode: false,
  settingsMap: {},
  pagePropertyValues: {},
  folderPropertyTypes: {},
  tableLoadingMap: {},
  tableRunControlMap: {},
  tableRunHistoryMap: {},
  notFoundTables: {},
  connectorStatusMap: {},
  noteEdits: {},
  tableLogsMap: {},
  tableRunResponses: {},
  roles: [],
  workspaceUsers: [],
  allUsers: [],
  notebookPages: {},
  apps: [],
  allWorkspaces: [],
  selectedPage: { contentType: PageContentType.LOADING_ACCOUNT },
  chatDrawerOpen: false,
  sideNavOpen: false,
  messages: [],
  connectors: [],
  drawerWidth: "15rem",
  tables: {},
  connectorTables: {},
  connectorTypes: [],
  chatLoading: true,
  tablePages: {},
  pushConnectors: {},
  pushConnectorForm: initialPushConnectorForm,
  tableSpecs: {},
  pageViews: {},
  pageToPageViews: {},
  pageViewSettings: {},
  charts: {},
  fullTables: {},
  connectorConfigUrls: {},
  isShiftDown: false,
  publishedPagesMap: {},
  components: initComponentMap,
  serviceAccounts: initServiceAccountMap,
  protocols: initProtocolMap,
  implementations: {},
  pageComponents: {},
  pageImplementations: {},
  pageSubPagesMap: {},
  logStreamMap: {},
  httpHookMap: {},
  subscriptionMap: {},
  subscriptionRefreshKey: 0,
  aiPanelOpen: false,
  aiPanelFullscreen: false,
  aiEvents: [],
  aiConnectionStatus: ConnectionStatus.DISCONNECTED,
};

export type AddTablePageConfig = {
  tableId: string;
  index: number;
  rows: string[][];
};

export const reducer: (
  state: MainState | undefined,
  action: any,
) => MainState = (state = initialState, action) => {
  switch (action.type as ActionType) {
    case ActionType.SET_PAGE_BLOCKS:
      return setPageBlocksReducer(state, action);
    case ActionType.SET_MULTIPLE_PAGE_BLOCKS:
      return setMultiplePageBlocksReducer(state, action);
    case ActionType.SET_FOLDER_PRIMARY_KEY:
      return setFolderPrimaryKeyAction(state, action);
    case ActionType.SET_FOLDER_SEC_INDEXES:
      return setFolderSecIndexesReducer(state, action);
    case ActionType.ADD_REGISTRATIONS:
      return addRegistrationsReducer(state, action);
    case ActionType.ADD_CODE_BLOCKS:
      return addCodeBlocksReducer(state, action);
    case ActionType.ADD_SETTINGS:
      return addSettingsReducer(state, action);
    case ActionType.SET_PAGE_PROPERTY_VALUES:
      return setPagePropertyValues(state, action);
    case ActionType.SET_PAGE_PROPS:
      return setPagePropsReducer(state, action as SetPagePropsAction);
    case ActionType.DELETE_PROPERTY:
      return deletePropertyTypeReducer(state, action);
    case ActionType.SET_SELECTED_PROPERTY:
      return setSelectedPropertyReducer(state, action);
    case ActionType.SET_PAGE_NAME:
      return setPageNameReducer(state, action);
    case ActionType.SET_FOLDER_PROPERTIES:
      return setFolderPropertiesReducer(state, action);
    case ActionType.MOVE_NOTE_BLOCK:
      return moveNoteBlockReducer(state, action);
    case ActionType.FLUSH_NOTE_EDITS:
      return flushNoteEditsReducer(state, action);
    case ActionType.SET_BLOCK_INDENT_LEVEL:
      return setBlockIndentLevelReducer(state, action);
    case ActionType.SET_NOTE_EDIT:
      return setNoteEditReducer(state, action);
    case ActionType.DELETE_BLOCK:
      return deleteBlockReducer(state, action);
    case ActionType.FOCUS_PREVIOUS_BLOCK:
      return focusPreviousBlockAction(state, action);
    case ActionType.FOCUS_NEXT_BLOCK:
      return focusNextBlockReducer(state, action);
    case ActionType.SET_BLOCK_TO_FOCUS:
      return blockToFocusReducer(state, action);
    case ActionType.INSERT_BLOCK:
      return insertBlockReducer(state, action);
    case ActionType.SET_TABLE_LOADING:
      return setTableLoadingReducer(state, action);
    case ActionType.REMOVE_NOTEBOOK_PAGES:
      return removeNotebookPagesReducer(state, action);
    case ActionType.ADD_NOTEBOOK_PAGES:
      return addNotebookPagesReducer(state, action);
    case ActionType.SET_TABLE_RUN_CONTROL:
      const tableRunControlMap = state.tableRunControlMap;
      const newTableRunControlMap = {
        ...tableRunControlMap,
        [action.tableId]: action.runControl,
      };
      return { ...state, tableRunControlMap: newTableRunControlMap };
    case ActionType.SET_TABLE_RUN_HISTORY_MAP:
      return { ...state, tableRunHistoryMap: action.tableRunHistoryMap };
    case ActionType.ADD_NOT_FOUND_TABLES:
      const notFoundTables = action.notFoundTables as TableNotFound[];
      const notFoundEntries = Object.fromEntries(
        notFoundTables.map((t) => [t["table-id"], t]),
      );
      const newNotFoundTables = { ...state.notFoundTables, ...notFoundEntries };
      return { ...state, notFoundTables: newNotFoundTables };
    case ActionType.SET_SELECTED_PULL_CONNECTOR:
      return { ...state, selectedPullConnector: action.selectedPullConnector };
    case ActionType.SET_CONNECTOR_STATUS_MAP:
      return { ...state, connectorStatusMap: action.connectorStatusMap };
    case ActionType.SET_NOTE_EDITS:
      return { ...state, noteEdits: action.noteEdits };
    case ActionType.SET_TABLE_LOGS_MAP:
      return { ...state, tableLogsMap: action.tableLogsMap };
    case ActionType.SET_TABLE_RUN_RESPONSES:
      return { ...state, tableRunResponses: action.tableRunResponses };
    case ActionType.SET_APP_OWNER:
      return { ...state, appOwner: action.appOwner };
    case ActionType.SET_STRIPE_SUBSCRIPTION:
      return { ...state, stripeSubscription: action.stripeSubscription };
    case ActionType.SET_STRIPE_CUSTOMER:
      return { ...state, stripeCustomer: action.stripeCustomer };
    case ActionType.SET_PAYMENT_METHODS:
      return { ...state, paymentMethods: action.paymentMethods };
    case ActionType.SET_ROLES:
      return { ...state, roles: action.roles };
    case ActionType.SET_ALL_WORKSPACES:
      return { ...state, allWorkspaces: action.allWorkspaces };
    case ActionType.SET_WORKSPACE_USERS:
      return { ...state, workspaceUsers: action.workspaceUsers };
    case ActionType.SET_ALL_USERS:
      return { ...state, allUsers: action.allUsers };
    case ActionType.SET_NOTEBOOK:
      return { ...state, notebook: action.notebook };
    case ActionType.SET_APP_ID:
      return setAppIdReducer(state, action);
    case ActionType.SET_APPS:
      return { ...state, apps: action.apps };
    case ActionType.SET_SELECTED_PAGE:
      return { ...state, selectedPage: action.selectedPage };
    case ActionType.SET_CHAT_ID:
      return setIdReducer(state, action);
    case ActionType.SET_CHAT_DRAWER_OPEN:
      return { ...state, chatDrawerOpen: action.chatDrawerOpen };
    case ActionType.SET_SIDE_NAV_OPEN:
      return { ...state, sideNavOpen: action.sideNavOpen };
    case ActionType.ADD_MESSAGE:
      return { ...state, messages: [...state.messages, action.message] };
    case ActionType.SET_MESSAGES:
      return { ...state, messages: action.messages };
    case ActionType.SET_CONNECTORS:
      return { ...state, connectors: action.connectors };
    case ActionType.ADD_TABLES:
      const tableEntries = R.map(
        (table: any) => [table["table-id"], table],
        action.tables,
      );
      const addTableObject = Object.fromEntries(tableEntries);
      const newTables = { ...state.tables, ...addTableObject };
      return { ...state, tables: newTables };
    case ActionType.ADD_TABLE_PAGE:
      const actionAddTablePage = action as AddTablePageConfig;
      const { tableId, index, rows } = actionAddTablePage;
      const currentTablePagesEntry = state.tablePages[tableId] ?? {};
      const newTablePagesEntry = {
        ...currentTablePagesEntry,
        [index]: rows,
      };
      const newTablePages = {
        ...state.tablePages,
        [tableId]: newTablePagesEntry,
      };
      return { ...state, tablePages: newTablePages };
    case ActionType.ASSOCIATE_CONNECTOR_TABLES:
      const newConnectorTables = {
        ...state.connectorTables,
        ...action.connectorTables,
      };
      return { ...state, connectorTables: newConnectorTables };
    case ActionType.SET_CHAT_LOADING:
      return { ...state, chatLoading: action.chatLoading };
    case ActionType.SET_CONNECTOR_TYPES:
      return { ...state, connectorTypes: action.connectorTypes };
    case ActionType.SET_SELECTED_CONNECT_CARD:
      return { ...state, selectedConnectCard: action.selectedConnectCard };
    case ActionType.SET_SELECTED_CONNECTOR_TYPE:
      return { ...state, selectedConnectorType: action.selectedConnectorType };
    case ActionType.SET_AUTH_TOKEN:
      return { ...state, authToken: action.authToken };
    case ActionType.SET_PUSH_CONNECTORS:
      return { ...state, pushConnectors: action.pushConnectors };
    case ActionType.SET_SELECTED_PUSH_CONNECTOR:
      return {
        ...state,
        selectedPushConnector: action.selectedPushConnector,
        selectedPage: { contentType: PageContentType.VIEW_PUSH_CONNECTOR },
      };
    case ActionType.SET_PUSH_CONNECTOR_FORM_DATA:
      return {
        ...state,
        pushConnectorForm: action.pushConnectorForm,
      };
    case ActionType.SET_INITIAL_PAGE_LOADING:
      return { ...state, initialPageLoaded: action.initialPageLoaded };
    case ActionType.SET_NO_WORKSPACES:
      return action.noWorkspaces
        ? {
            ...state,
            noWorkspaces: action.noWorkspaces,
            selectedPage: { contentType: PageContentType.NO_WORKSPACE },
          }
        : { ...state, noWorkspaces: action.noWorkspaces };
    case ActionType.SET_TABLE_SPECS:
      return { ...state, tableSpecs: action.tableSpecs };
    case ActionType.SET_SELECTED_NOTEBOOK_PAGE_ID:
      return setSelectedNotebookPageIdReducer(state, action);
    case ActionType.SET_PAGE_PAGE_VIEWS:
      return setPagePageViewsReducer(state, action);
    case ActionType.SET_VIEW_SETTING:
      return setViewSettingReducer(state, action as any);
    case ActionType.SET_SELECTED_VIEW_ID:
      return setSelectedViewIdReducer(state, action);
    case ActionType.ADD_CHARTS:
      return addChartsReducer(state, action);
    case ActionType.ADD_FULL_TABLES:
      return addFullTablesReducer(state, action);
    case ActionType.ADD_PULL_CONNECTOR_STATUS:
      return addPullConnectorStatusAction(state, action);
    case ActionType.ADD_PULL_CONNECTOR_URL_CONFIGS:
      return addPullConnectorUrlConfigsAction(state, action);
    case ActionType.SWAP_DATABASE_VIEWS:
      return swapPageViewsReducer(state, action);
    case ActionType.SET_SHIFT_DOWN:
      return setShiftDownReducer(state, action);
    case ActionType.SET_PAGE_PUBLISHED:
      return setPagePublishedReducer(state, action);
    case ActionType.SET_SELECTED_PULL_CONNECTOR_TABLE:
      return setSelectedPullConnectorTableAction(state, action);
    case ActionType.ADD_COMPONENTS:
      return addComponentsReducer(state, action);
    case ActionType.ADD_SERVICE_ACCOUNTS:
      return addServiceAccountsReducer(state, action);
    case ActionType.ADD_PROTOCOLS:
      return addProtocolsReducer(state, action);
    case ActionType.ADD_IMPLEMENTATIONS:
      return addImplementationsReducer(state, action);
    case ActionType.SET_PAGE_COMPONENTS:
      return setPageComponentsReducer(state, action);
    case ActionType.ADD_PAGE_IMPLEMENTATIONS:
      return addPageImplementationsReducer(state, action);
    case ActionType.ADD_SUB_PAGES:
      return addSubPagesReducer(state, action);
    case ActionType.ADD_LOG_STREAMS:
      return addLogStreamsReducer(state, action);
    case ActionType.ADD_HTTP_HOOKS:
      return addHttpHooksReducer(state, action);
    case ActionType.ADD_SUBSCRIPTIONS:
      return addSubscriptionsReducer(state, action as AddSubscriptionsAction);
    case ActionType.CLEAR_SUBSCRIPTION_MAP:
      return clearSubscriptionMapReducer(state);
    case ActionType.INCREMENT_SUBSCRIPTION_REFRESH:
      return incrementSubscriptionRefreshReducer(state);
    case ActionType.ADD_PACKAGES:
      return addPackagesReducer(state, action);
    case ActionType.SET_INSTALLED_PACKAGES:
      return setInstalledPackagesReducer(state, action);
    case ActionType.SET_AI_PANEL_OPEN:
      return { ...state, aiPanelOpen: action.aiPanelOpen };
    case ActionType.SET_AI_PANEL_FULLSCREEN:
      return { ...state, aiPanelFullscreen: action.aiPanelFullscreen };
    case ActionType.ADD_AI_EVENT:
      return { ...state, aiEvents: [...state.aiEvents, action.event] };
    case ActionType.SET_AI_EVENTS:
      return { ...state, aiEvents: action.events };
    case ActionType.CLEAR_AI_EVENTS:
      return { ...state, aiEvents: [] };
    case ActionType.SET_AI_CONNECTION_STATUS:
      return { ...state, aiConnectionStatus: action.status };
  }
  return state;
};

export const store = configureStore({ reducer: { pages: pagesReducer, tables: tablesReducer, connectors: connectorsReducer, settings: settingsReducer, ui: uiReducer } });

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export default store;
